import c from 'classnames';
import { addWeeks, format } from 'date-fns';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { kitResultsViewable, parseDate } from '@repo/utils';
import { useTinyAccountForHome } from 'hooks/useTinyAccountForHome';
import { getHomePageTinyAccountId } from 'store/home';
import { getImpersonateId } from 'store/user';

import { Card, Typography } from 'components';

import styles from './ProcessingYourSample.module.scss';

export const ProcessingYourSample = () => {
  const tinyAccountId = useSelector(getHomePageTinyAccountId);
  const impersonateId = useSelector(getImpersonateId);

  const { latestKit, getDisplayName } = useTinyAccountForHome(tinyAccountId);

  const processingWeeksMin = 3;
  const processingWeeksMax = 4;

  const estimatedEarliest = latestKit?.date_delivered_to_lab
    ? addWeeks(parseDate(latestKit?.date_delivered_to_lab), processingWeeksMin)
    : null;

  const estimatedLatest = latestKit?.date_delivered_to_lab
    ? addWeeks(parseDate(latestKit?.date_delivered_to_lab), processingWeeksMax)
    : null;

  const message = useMemo(() => {
    const processingTime = '3-4 weeks';
    if (latestKit?.date_sequences_delivered)
      return (
        <Typography variant='body-s' responsive serif>
          Our lab has finished processing your sample and your results will be
          ready soon.
        </Typography>
      );
    if (latestKit?.date_delivered_to_lab)
      return (
        <Typography variant='body-s' responsive serif>
          Your sample was delivered to our lab on{' '}
          {format(parseDate(latestKit.date_delivered_to_lab), 'MMMM d')}. We
          estimate {latestKit?.practitioner_id ? 'your practitioner' : 'you'}{' '}
          will have your results between {format(estimatedEarliest!, 'MMMM d')}{' '}
          and {format(estimatedLatest!, 'MMMM d')}.
        </Typography>
      );
    if (latestKit?.date_in_transit_to_lab)
      return (
        <Typography variant='body-s' responsive serif>
          Your sample is en route to our lab. Once received by the lab, your
          sample will take {processingTime} to process.
        </Typography>
      );
    return (
      <Typography variant='body-s' responsive serif>
        Please mail your sample using the box provided. The prepaid return label
        is already attached to the outside of the box. To reveal it, simply
        remove the outer sleeve. Once received by the lab, your sample will take{' '}
        {processingTime} to process.
      </Typography>
    );
  }, [latestKit]);

  //Only show this card after surveys and until results are ready
  if (
    !latestKit?.date_survey_completed ||
    kitResultsViewable(latestKit, !!impersonateId)
  )
    return <></>;
  return (
    <Card variant='wrapper' className={c(styles.card, 'orange-border-left')}>
      <div className='flex align-items-center flex-column gap-2 text-center'>
        {!!estimatedEarliest && !!estimatedLatest && (
          <>
            <Typography variant='label'>Estimated results</Typography>
            <Typography variant='heading-l' className='pb-4'>
              {format(estimatedEarliest, 'MMM d')} -{' '}
              {format(estimatedLatest, 'MMM d')}
            </Typography>
          </>
        )}
        {!!latestKit?.date_delivered_to_lab && (
          <Typography variant='heading-m'>
            {`We’re processing ${getDisplayName(true)} sample`}
          </Typography>
        )}
        {message}
      </div>
    </Card>
  );
};
